import type { ReactElement } from 'react';

import { Field, formatDateTime } from '@/components/vacancy-complaints/Field';
import {
    CAFE_ROLE_LABEL,
    COLLABORATION_FORMAT_LABEL,
    EMPLOYMENT_KIND_LABEL,
    formatRateRange,
    VACANCY_STATUS_LABEL
} from '@/lib/vacancyComplaintLabels';
import type { ComplaintVacancyFull } from '@/lib/vacancyComplaintsSchemas';

const QUESTION_TYPE_LABEL: Record<
    ComplaintVacancyFull['screeningQuestions'][number]['type'],
    string
> = {
    yes_no: 'Да / Нет',
    choice: 'Выбор',
    short_text: 'Короткий текст'
};

interface VacancySectionProps {
    vacancy: ComplaintVacancyFull;
}

/**
 * Блок «Вакансия» детали жалобы — все публичные поля, как видит профи
 * (design-spec §2.5): роль/специализации/тип/вилка/формат/описание/статус +
 * точки с адресом + скрининг-вопросы. Без счётчиков откликов (внутренняя
 * кухня кафе — в контракте их нет). Free-text (описание/вопросы/варианты) —
 * plain-text рендер (SF-14).
 */
export function VacancySection({ vacancy }: VacancySectionProps): ReactElement {
    const specializations =
        vacancy.specializations.length > 0
            ? vacancy.specializations.map((s) => s.name).join(', ')
            : null;
    const collaborationFormat =
        vacancy.collaborationFormat !== null
            ? COLLABORATION_FORMAT_LABEL[vacancy.collaborationFormat]
            : null;

    return (
        <section data-testid="vacancy-complaint-vacancy">
            <h2 className="mb-3 text-base font-semibold text-gray-900">Вакансия</h2>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <Field
                    label="Заголовок"
                    value={vacancy.title}
                    wide
                    testId="vacancy-complaint-vacancy-title"
                />
                <Field label="Роль" value={CAFE_ROLE_LABEL[vacancy.role]} />
                <Field label="Специализации" value={specializations} />
                <Field
                    label="Тип занятости"
                    value={EMPLOYMENT_KIND_LABEL[vacancy.employmentKind]}
                />
                <Field
                    label="Ставка"
                    value={formatRateRange(
                        vacancy.rateFromKop,
                        vacancy.rateToKop,
                        vacancy.employmentKind
                    )}
                />
                <Field label="Формат сотрудничества" value={collaborationFormat} />
                <Field
                    label="Статус вакансии"
                    value={VACANCY_STATUS_LABEL[vacancy.status]}
                    testId="vacancy-complaint-vacancy-status"
                />
                <Field label="Кафе" value={vacancy.cafeName} />
                <Field label="Опубликована" value={formatDateTime(vacancy.publishedAt)} />
                <Field
                    label="Описание"
                    value={vacancy.description}
                    wide
                    multiline
                    testId="vacancy-complaint-vacancy-description"
                />
            </dl>

            <div className="mt-4">
                <dt className="text-xs font-medium uppercase text-gray-500">Где работать</dt>
                {vacancy.branches.length === 0 ? (
                    <p className="text-sm text-gray-900">Вся сеть</p>
                ) : (
                    <ul className="mt-1 space-y-1 text-sm text-gray-900">
                        {vacancy.branches.map((branch, index) => (
                            <li key={`${branch.name}-${index}`} className="break-words">
                                {branch.name}
                                {branch.city !== null && branch.city !== ''
                                    ? ` · ${branch.city}`
                                    : ''}
                                {branch.addressLine !== '' ? ` · ${branch.addressLine}` : ''}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {vacancy.screeningQuestions.length > 0 && (
                <div className="mt-4" data-testid="vacancy-complaint-vacancy-questions">
                    <dt className="text-xs font-medium uppercase text-gray-500">
                        Скрининг-вопросы
                    </dt>
                    <ol className="mt-1 list-decimal space-y-2 pl-5 text-sm text-gray-900">
                        {vacancy.screeningQuestions.map((question) => (
                            <li key={question.id} className="break-words">
                                <span className="whitespace-pre-wrap">{question.text}</span>
                                <span className="ml-2 text-xs text-gray-500">
                                    ({QUESTION_TYPE_LABEL[question.type]})
                                </span>
                                {question.options !== undefined &&
                                    question.options.length > 0 && (
                                    <ul className="mt-1 list-disc pl-5 text-xs text-gray-600">
                                        {question.options.map((option, optIndex) => (
                                            <li
                                                key={`${question.id}-opt-${optIndex}`}
                                                className="break-words"
                                            >
                                                {option}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </section>
    );
}
